import React from 'react';
import ReactTooltip from 'react-tooltip';
import cn from 'class-names';

import hexToRgba from '@utils/hexToRgba';
import Display from '@components/Display/Display';

import './ColoredStatusToggler.scss';
import { SVGIcon, SvgIconType } from '@uikit/old/Icon/Icon';

const TogglerIcon = React.memo(
  ({
    color = '#fff',
    name,
    iconSize,
    nameString,
  }: {
    color: string;
    name?: any;
    iconSize: number;
    nameString?: string;
  }): JSX.Element => {
    return !nameString ? (
      <SVGIcon icon={name} color={color} size={iconSize} />
    ) : (
      <p style={{ background: color }} className="icon-name">{nameString}</p>
    );
  }
);

function ColoredStatusToggler({
  name = '',
  isActive,
  isLoading = false,
  isView = false,
  color = '#fff',
  onChange,
  onApprove,
  onReject,
  title,
  status,
  titleColor,
  iconColor,
  icon,
  iconSize,
  id,
  clickable = true,
  showConfirm,
  nameString,
  isIconcircle,
  tooltipText,
  testId,
}: {
  name?: string;
  isActive: boolean;
  isLoading?: boolean;
  isView?: boolean;
  color?: string;
  onChange?: (boolean) => void;
  onApprove?: () => void;
  onReject?: () => void;
  title?: string;
  status?: number;
  titleColor?: string;
  iconColor?: string;
  icon?: SvgIconType;
  iconSize?: number;
  id?: number;
  clickable?: boolean;
  showConfirm?: boolean;
  nameString?: string;
  isIconcircle?: boolean;
  /**
   * Overrides the default tooltip, which is derived from the numeric `status` with campaign
   * wording (launch / pause / unpause). Entities whose status enum does not line up with
   * 0 / 1 / 2 (publisher websites, for one) pass their own text.
   */
  tooltipText?: string;
  /** data-testid on the clickable toggler (with `data-status`) */
  testId?: string;
}): JSX.Element {
  const cnToggle = cn('colored-toggler', {
    'colored-toggler__big-size': title,
    'colored-toggler__fit-size': isView,
    'colored-toggler__approval': showConfirm,
    'colored-toggler__not-clickable': !clickable,
  });
  const cnToggleSlider = cn('colored-toggler__slider', {
    'colored-toggler__slider--active': isActive,
    'colored-toggler__slider--loading': isLoading,
    'colored-toggler__slider--view': isView,
    'colored-toggler__slider--circled': isIconcircle,
  });

  const getIcon = () => {
    switch (status) {
      case 0:
        return 'lightningArrow';
      case 1:
        return 'pauseIcon';
      case 2:
        return 'playArrow';
      case 3:
        return 'crossBlack';
      case 4:
        return 'checkmarkIcon';
    }
  };

  const getTitle = () => {
    if (tooltipText !== undefined) {
      return tooltipText;
    }

    switch (status) {
      case 0:
        return `Click to launch the ${name}`;
      case 1:
        return `Click to pause the ${name}`;
      case 2:
        return `Click to unpause the ${name}`;
      default:
        return '';
    }
  };

  const handleChange = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (!isLoading && clickable) {
      onChange(!isActive);
    }
  };

  const tooltipTitle = getTitle();

  return (
    <>
      <div
        className={cnToggle}
        style={{
          background: hexToRgba(color, '1'),
        }}
        onClick={handleChange}
        data-testid={testId}
        data-status={status}
        data-for={`colored-status-button-${id}`}
        data-tip=""
      >
        {title && (
          <span
            className={`colored-toggler__title ${!isActive ? 'right' : ''}`}
            style={{ color: titleColor }}
          >
            {title}
          </span>
        )}
        {showConfirm ? (
          <>
            <div
              className={`${cnToggleSlider} colored-toggler__slider--reject`}
              style={{ background: '#ED483D' }}
              onClick={onReject}
            >
              <TogglerIcon
                color="#fff"
                nameString={nameString}
                name="crossBlack"
                iconSize={iconSize}
              />
            </div>
            <div
              className={`${cnToggleSlider} colored-toggler__slider--confirm`}
              style={{ background: '#23E771' }}
              onClick={onApprove}
            >
              <TogglerIcon
                color="#fff"
                nameString={nameString}
                name="checkmarkIcon"
                iconSize={iconSize}
              />
            </div>
          </>
        ) : (
          <div
            className={cnToggleSlider}
            style={{
              background: isActive
                ? title
                  ? titleColor
                  : status === 4
                    ? '#ffffff'
                    : color
                : isIconcircle
                  ? ''
                  : titleColor,
            }}
          >
            <Display isVisible={!isLoading}>
              <TogglerIcon
                color={title ? iconColor : '#A3B2C9'}
                name={icon || getIcon()}
                iconSize={iconSize}
              />
            </Display>

            <Display isVisible={isView}>
              <TogglerIcon color={iconColor} iconSize={iconSize} nameString={nameString} />
            </Display>
          </div>
        )}
      </div>
      {clickable && tooltipTitle.length ? (
        <ReactTooltip
          id={`colored-status-button-${id}`}
          place="top"
          effect="solid"
          arrowColor="transparent"
        >
          <div>{getTitle()}</div>
        </ReactTooltip>
      ) : null}
    </>
  );
}

export default ColoredStatusToggler;
